/**
 * `ServerGameSession` — server-side game session over a pluggable transport.
 *
 * The session owns the listening socket, accepts client connections, drives
 * the handshake, tracks per-client transport stats, and routes inbound
 * RPCs / replication acks back through the `IGameSession` delegates.
 *
 * Transport is pluggable via `INetworkServer` / `INetworkConnection`; the
 * default implementation uses `ws` over WebSocket.
 */

import { type RawData, WebSocket, WebSocketServer } from 'ws';

import { NET_PING_INTERVAL_SEC } from '../../const.js';
import { Delegate } from '../../utils/Delegate.js';
import { shortUUID } from '../../utils/uuid.js';
import { IGameSession } from '../interface.js';
import { decodeRpcHeader, encodeRpcPayload } from '../RpcBinaryCodec.js';

import { AbstractMeteredConnection } from './AbstractMeteredConnection.js';
import {
  decodeWireMessage,
  encodeWireMessage,
  type HandshakeRequestData,
  type HandshakeResponseData,
  MessageType,
  type PingMessage,
  type ReplicationAckMessage,
  type ReplicationMessage,
  type RequestMessage,
  type ResponseMessage,
  type RpcMessage
} from './NetworkProtocol.js';

import type {
  ClientState,
  ConnectionStatsSnapshot,
  GameSessionDescription,
  INetworkConnection,
  INetworkServer,
  JoinParams,
  ReplicationPacket
} from '../interface.js';
import type { ClientId, ObjectId } from '../interface.js';

function rawDataToUint8Array(data: RawData): Uint8Array {
  if (typeof data === 'string') {
    throw new Error('Expected binary WebSocket frame');
  }
  if (Buffer.isBuffer(data)) {
    return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  }
  if (data instanceof ArrayBuffer) {
    return new Uint8Array(data);
  }
  return new Uint8Array(Buffer.concat(data));
}

/**
 * Factory that builds an `INetworkServer`. Used by `ServerGameSession`
 * to remain transport-agnostic.
 */
export type NetworkServerFactory = () => INetworkServer;

/**
 * Default `INetworkConnection` for sockets accepted by `WebSocketNetworkServer`.
 *
 * Server-accepted connections are delivered already-open, so `connect()` is a
 * no-op and `onOpen` is not invoked. Extends `AbstractMeteredConnection` so
 * stats are tracked and the underlying TCP socket's byte counters are exposed
 * under `extra` when available.
 */
class WebSocketServerConnection extends AbstractMeteredConnection {
  constructor(private readonly ws: WebSocket) {
    super();
    this.markConnected();

    ws.on('message', (data: RawData) => {
      const bytes = rawDataToUint8Array(data);
      this.trackReceived(bytes.byteLength);
      this.onMessage.invoke(bytes);
    });

    ws.on('close', (code: number, reason: Buffer) => {
      this.onClose.invoke(code, reason.toString());
    });

    ws.on('error', (error: Error) => {
      this.onError.invoke(error);
    });
  }

  public isOpen(): boolean {
    return this.ws.readyState === WebSocket.OPEN;
  }

  public connect(): void {
    // No-op: server-accepted connections are delivered already open.
  }

  public send(data: Uint8Array): void {
    this.trackSent(data.byteLength);
    this.ws.send(data);
  }

  public close(code?: number, reason?: string): void {
    this.ws.close(code, reason);
  }

  protected override collectExtra(): Record<string, number> | undefined {
    const socket = (this.ws as unknown as { _socket?: { bytesRead?: number; bytesWritten?: number } })._socket;
    if (!socket) {
      return undefined;
    }
    const extra: Record<string, number> = {};
    if (typeof socket.bytesRead === 'number') {
      extra.tcpBytesRead = socket.bytesRead;
    }
    if (typeof socket.bytesWritten === 'number') {
      extra.tcpBytesWritten = socket.bytesWritten;
    }
    return Object.keys(extra).length > 0 ? extra : undefined;
  }
}

/**
 * Default `INetworkServer` backed by the `ws` `WebSocketServer`. Each
 * accepted connection is wrapped as a {@link WebSocketServerConnection}.
 */
export class WebSocketNetworkServer implements INetworkServer {
  public readonly onConnection: Delegate<[INetworkConnection], void> = new Delegate();
  public readonly onError: Delegate<[Error], void> = new Delegate();

  private wss: WebSocketServer | null = null;

  public isListening(): boolean {
    return this.wss !== null;
  }

  public listen(port: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const wss = new WebSocketServer({ port });
      let started = false;

      wss.on('error', (error) => {
        if (!started) {
          reject(error);
        } else {
          this.onError.invoke(error);
        }
      });

      wss.on('listening', () => {
        started = true;
        this.wss = wss;
        resolve();
      });

      wss.on('connection', (ws: WebSocket) => {
        const connection = new WebSocketServerConnection(ws);
        this.onConnection.invoke(connection);
      });
    });
  }

  public close(): Promise<void> {
    return new Promise((resolve) => {
      const wss = this.wss;
      if (!wss) {
        resolve();
        return;
      }
      this.wss = null;
      for (const client of wss.clients) {
        if (client.readyState === WebSocket.OPEN) {
          client.close();
        }
      }
      wss.close(() => resolve());
    });
  }
}

/**
 * Options for constructing a {@link ServerGameSession}.
 */
export interface ServerGameSessionOptions {
  /** Game ID this server is hosting (build-time, not part of the wire). */
  gameId: string;
  /** Optional human-readable session name. */
  sessionName?: string;
  /** Optional transport factory; defaults to a `ws` WebSocket server. */
  serverFactory?: NetworkServerFactory;
  /** Optional engine version reported back to clients on handshake. */
  engineVersion?: string;
  /** Optional game version reported back to clients on handshake. */
  gameVersion?: string;
}

/**
 * Server-side `IGameSession`. The whole server process is one session, so
 * any connected client is implicitly part of this session as soon as the
 * handshake completes.
 */
export class ServerGameSession extends IGameSession {
  private readonly serverFactory: NetworkServerFactory;
  private readonly engineVersion: string;
  private readonly gameVersion: string;

  private networkServer: INetworkServer | null = null;
  private port: number = 0;
  private pingTimer: NodeJS.Timeout | null = null;

  /** Per-client state and connection bookkeeping. */
  private clientStates: Map<ClientId, ClientState> = new Map();
  private clientIdToConnection: Map<ClientId, INetworkConnection> = new Map();
  private connectionToClientId: Map<INetworkConnection, ClientId> = new Map();
  /** Connections that opened but have not yet completed the handshake. */
  private pendingConnections: Set<INetworkConnection> = new Set();

  constructor(options: ServerGameSessionOptions) {
    super({
      gameId: options.gameId,
      sessionName: options.sessionName
    });
    this.serverFactory = options.serverFactory ?? (() => new WebSocketNetworkServer());
    this.engineVersion = options.engineVersion ?? '1.0.0';
    this.gameVersion = options.gameVersion ?? '1.0.0';
  }

  //==================================================================
  // IGameSession overrides
  //==================================================================

  public override async startListening(portRange: [number, number]): Promise<number> {
    const [minPort, maxPort] = portRange;

    for (let port = minPort; port <= maxPort; port++) {
      try {
        await this.tryStartServer(port);
        this.port = port;
        console.log(`[ServerGameSession] Server started on port ${port}`);
        return port;
      } catch (error) {
        if (port === maxPort) {
          throw new Error(`Failed to start server on any port in range ${minPort}-${maxPort}`);
        }
        // Try next port
      }
    }

    throw new Error(`Failed to start server on any port in range ${minPort}-${maxPort}`);
  }

  public override async clientHeartBeat(clientId: ClientId): Promise<boolean> {
    this.clientStates.set(clientId, { lastHeartBeatTime: Date.now() });
    return true;
  }

  public override async getClients(): Promise<Record<ClientId, ClientState>> {
    const result: Record<ClientId, ClientState> = {};
    for (const [clientId, state] of this.clientStates.entries()) {
      result[clientId] = state;
    }
    return result;
  }

  public override getClientStats(): Record<ClientId, ConnectionStatsSnapshot> {
    const out: Record<ClientId, ConnectionStatsSnapshot> = {};
    for (const [clientId, connection] of this.clientIdToConnection) {
      out[clientId] = connection.getStats();
    }
    return out;
  }

  public override sendClientRpc(
    objectId: ObjectId,
    methodName: string,
    argsPayload: Uint8Array,
    _reliable: boolean,
    targetClients: ClientId[] | null,
    netSubId?: string
  ): void {
    const rpcMessage: RpcMessage = {
      type: MessageType.RPC,
      payload: encodeRpcPayload(objectId, methodName, argsPayload, netSubId),
    };

    const connections: INetworkConnection[] = targetClients
      ? targetClients
        .map((id) => this.clientIdToConnection.get(id))
        .filter((c): c is INetworkConnection => c !== undefined)
      : Array.from(this.clientIdToConnection.values());

    const payload = encodeWireMessage(rpcMessage);
    for (const connection of connections) {
      if (connection.isOpen()) {
        connection.send(payload);
      }
    }
  }

  public override sendReplicationPacket(clientId: ClientId, packet: ReplicationPacket): void {
    const connection = this.clientIdToConnection.get(clientId);
    if (!connection?.isOpen()) {
      console.warn(`[ServerGameSession] Client ${clientId} not found or connection closed`);
      return;
    }
    const message: ReplicationMessage = {
      type: MessageType.REPLICATION,
      data: packet
    };
    try {
      connection.send(encodeWireMessage(message));
    } catch (error) {
      console.warn(`[ServerGameSession] Failed to send replication packet to client ${clientId}:`, error);
    }
  }

  public override destroy(): void {
    this.stopPingLoop();

    for (const connection of this.clientIdToConnection.values()) {
      if (connection.isOpen()) {
        connection.close();
      }
    }
    for (const connection of this.pendingConnections) {
      if (connection.isOpen()) {
        connection.close();
      }
    }
    this.clientIdToConnection.clear();
    this.connectionToClientId.clear();
    this.pendingConnections.clear();
    this.clientStates.clear();

    if (this.networkServer) {
      void this.networkServer.close();
      this.networkServer = null;
    }

    console.log('[ServerGameSession] Destroyed — all connections closed');
  }

  /**
   * Stop accepting new connections and close existing ones, without
   * tearing down the session bookkeeping (use {@link destroy} for full
   * teardown).
   */
  public async stopServer(): Promise<void> {
    this.stopPingLoop();
    if (this.networkServer) {
      await this.networkServer.close();
      this.networkServer = null;
      console.log('[ServerGameSession] Server stopped');
    }
  }

  /** Returns the port the server is listening on (0 if not started). */
  public getPort(): number {
    return this.port;
  }

  /** Per-client connection stats by client ID, or `null` if unknown. */
  public getClientStatsById(clientId: ClientId): ConnectionStatsSnapshot | null {
    const connection = this.clientIdToConnection.get(clientId);
    return connection ? connection.getStats() : null;
  }

  //==================================================================
  // Transport plumbing
  //==================================================================

  private async tryStartServer(port: number): Promise<void> {
    const server = this.serverFactory();

    server.onConnection.add((connection) => {
      this.handleConnection(connection);
    });
    server.onError.add((error) => {
      console.error('[ServerGameSession] Network server error:', error);
    });

    await server.listen(port);
    this.networkServer = server;
    console.log(`[ServerGameSession] ✓ Server listening on port ${port}`);

    this.startPingLoop();
  }

  /**
   * Periodic application-level PING to every connected client so latency
   * stats include app-loop processing time, not just transport RTT.
   */
  private startPingLoop(): void {
    if (this.pingTimer) {
      return;
    }
    this.pingTimer = setInterval(() => {
      if (this.clientIdToConnection.size === 0) {
        return;
      }
      for (const connection of this.clientIdToConnection.values()) {
        if (!connection.isOpen()) {
          continue;
        }
        const ping: PingMessage = {
          type: MessageType.PING,
          pingId: shortUUID(),
          sentAt: Date.now()
        };
        try {
          connection.send(encodeWireMessage(ping));
        } catch (error) {
          console.warn('[ServerGameSession] Failed to send ping:', error);
        }
      }
    }, NET_PING_INTERVAL_SEC * 1000);
  }

  private stopPingLoop(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private handleConnection(connection: INetworkConnection): void {
    console.log('[ServerGameSession] ✓ Incoming connection (awaiting handshake)');
    this.pendingConnections.add(connection);

    connection.onMessage.add((rawMessage) => {
      try {
        const message = decodeWireMessage(rawMessage);
        this.handleMessage(connection, message);
      } catch (error) {
        console.error('[ServerGameSession] Failed to decode message:', error);
      }
    });

    connection.onClose.add(() => {
      this.handleClientDisconnect(connection);
    });

    connection.onError.add((error) => {
      console.error('[ServerGameSession] Connection error:', error);
    });
  }

  private handleClientDisconnect(connection: INetworkConnection): void {
    this.pendingConnections.delete(connection);
    const clientId = this.connectionToClientId.get(connection);
    if (clientId) {
      console.log(`[ServerGameSession] ✗ Client disconnected: ${clientId}`);
      this.connectionToClientId.delete(connection);
      this.clientIdToConnection.delete(clientId);
      this.clientStates.delete(clientId);
      this.handlePlayerLeft(clientId);
    } else {
      console.log('[ServerGameSession] ✗ Pending client disconnected before handshake');
    }
  }

  private handleMessage(connection: INetworkConnection, message: any): void {
    if (message?.type === MessageType.PING) {
      const ping = message as PingMessage;
      const pong: PingMessage = {
        type: MessageType.PONG,
        pingId: ping.pingId,
        sentAt: ping.sentAt
      };
      try {
        connection.send(encodeWireMessage(pong));
      } catch (error) {
        console.warn('[ServerGameSession] Failed to send pong:', error);
      }
      return;
    }
    if (message?.type === MessageType.PONG) {
      const pong = message as PingMessage;
      connection.recordRttSample(Date.now() - pong.sentAt);
      return;
    }

    if (message?.type === MessageType.HANDSHAKE) {
      this.handleHandshake(connection, message as RequestMessage);
      return;
    }

    // Past this point the connection must be authenticated (i.e. handshake done).
    const clientId = this.connectionToClientId.get(connection);
    if (!clientId) {
      console.warn(`[ServerGameSession] Dropping message of type ${message?.type} from unauthenticated connection`);
      return;
    }

    if (message?.type === MessageType.REPLICATION_ACK) {
      const envelope = message as ReplicationAckMessage;
      if (envelope.data) {
        this.onReplicationAckReceived.invoke(envelope.data, clientId);
      }
      return;
    }

    if (message?.type === MessageType.RPC) {
      this.handleClientRpc(connection, clientId, message as RpcMessage);
      return;
    }

    const request = message as RequestMessage;
    switch (request.type) {
      case MessageType.CLIENT_HEARTBEAT:
        void this.handleClientHeartbeat(connection, clientId, request);
        break;
      case MessageType.GET_CLIENTS:
        void this.handleGetClients(connection, request);
        break;
      case MessageType.DISCONNECT:
        void this.handleDisconnect(connection, clientId, request);
        break;
      default:
        this.sendError(connection, request.id ?? 'unknown', `Unknown message type: ${request.type}`, MessageType.HANDSHAKE);
    }
  }

  private handleHandshake(connection: INetworkConnection, message: RequestMessage): void {
    if (!this.pendingConnections.has(connection) && !this.connectionToClientId.has(connection)) {
      // Unknown state — close to be safe.
      console.warn('[ServerGameSession] Handshake from unknown connection — closing');
      connection.close();
      return;
    }
    if (this.connectionToClientId.has(connection)) {
      this.sendError(connection, message.id ?? 'handshake', 'Already handshaken', MessageType.HANDSHAKE);
      return;
    }

    const clientId: ClientId = `Client'${shortUUID()}'`;
    const data = (message.data ?? {}) as HandshakeRequestData;
    const joinParams: JoinParams = data.joinParams ?? {};

    // Register the connection under its new client ID.
    this.pendingConnections.delete(connection);
    this.connectionToClientId.set(connection, clientId);
    this.clientIdToConnection.set(clientId, connection);

    const sessionDescription: GameSessionDescription = this.getDescription();
    const responseData: HandshakeResponseData = {
      clientId,
      engineVersion: this.engineVersion,
      gameVersion: this.gameVersion,
      gameSession: sessionDescription
    };
    const response: ResponseMessage = {
      type: MessageType.HANDSHAKE,
      id: message.id ?? 'handshake',
      success: true,
      data: responseData
    };

    try {
      connection.send(encodeWireMessage(response));
    } catch (error) {
      console.error('[ServerGameSession] Failed to send handshake response:', error);
      connection.close();
      return;
    }

    console.log(`[ServerGameSession] ✓ Client handshake complete: ${clientId}`);

    // Fire join right after the handshake ack — the connection IS the join.
    this.handlePlayerJoined(clientId, joinParams);
  }

  private handleClientRpc(_connection: INetworkConnection, clientId: ClientId, message: RpcMessage): void {
    if (!message.payload) {
      console.warn(`[ServerGameSession] Received RPC from client ${clientId} with no payload — dropping`);
      return;
    }

    const { objectId, methodName, netSubId, reader } = decodeRpcHeader(message.payload);
    const argsPayload = reader.remainingBytes();

    this.onRPCReceived.invoke({ objectId, methodName, argsPayload, netSubId }, clientId);
  }

  private async handleClientHeartbeat(
    connection: INetworkConnection,
    clientId: ClientId,
    message: RequestMessage
  ): Promise<void> {
    const explicitId = (message.data?.clientId as ClientId | undefined) ?? clientId;
    const ok = await this.clientHeartBeat(explicitId);
    this.sendSuccess(connection, message.id, ok, MessageType.CLIENT_HEARTBEAT);
  }

  private async handleGetClients(connection: INetworkConnection, message: RequestMessage): Promise<void> {
    const clients = await this.getClients();
    this.sendSuccess(connection, message.id, clients, MessageType.GET_CLIENTS);
  }

  private async handleDisconnect(
    connection: INetworkConnection,
    clientId: ClientId,
    message: RequestMessage
  ): Promise<void> {
    console.log(`[ServerGameSession] Client ${clientId} requested disconnect`);
    this.sendSuccess(connection, message.id, {}, MessageType.DISCONNECT);
    connection.close();
  }

  private sendSuccess(connection: INetworkConnection, requestId: string, data: any, messageType: MessageType): void {
    const response: ResponseMessage = {
      type: messageType,
      id: requestId,
      success: true,
      data
    };
    try {
      connection.send(encodeWireMessage(response));
    } catch (error) {
      console.warn('[ServerGameSession] Failed to send success response:', error);
    }
  }

  private sendError(connection: INetworkConnection, requestId: string, errorMessage: string, messageType: MessageType): void {
    const response: ResponseMessage = {
      type: messageType,
      id: requestId,
      success: false,
      error: errorMessage
    };
    try {
      connection.send(encodeWireMessage(response));
    } catch (error) {
      console.warn('[ServerGameSession] Failed to send error response:', error);
    }
  }
}
